import {Inject, Injectable} from "@angular/core";
import {ACCOUNT_MANAGER_SERVICE} from "./account-manager/injection-token";
import {Role} from "../models/role";
import {environment} from "@environments/environment";
import {Account} from "../models/account";
import {RestApiManager} from "./rest-api-manager";
import {ReplaySubject} from "rxjs";
import {IAccountManagerService} from "./account-manager/iaccount-manager-service";
import {FieldService, UserFieldServiceAssignment} from '../models/field-service';
import {Paginator} from '../models/paginator';

@Injectable({
  providedIn: 'root'
})
export class AccountManagerService {

  public account: Account;
  public onLoginStatusChanged: ReplaySubject<boolean>;
  public fieldServiceAssignments: UserFieldServiceAssignment[];

  constructor(
    @Inject(ACCOUNT_MANAGER_SERVICE) private authProvider: IAccountManagerService,
    private restApiManager: RestApiManager
  ) {

    this.onLoginStatusChanged = new ReplaySubject<boolean>();

    // Init account
    this.account = new Account();

    authProvider.account = this.account;
    authProvider.onLoginStatusChanged = this.onLoginStatusChanged;
    authProvider.getRoles = this.getRoles;
    authProvider.isAdminOrExpert = this.isAdminOrExpert;

    if (authProvider.configureAccessTokenFetcher) authProvider.configureAccessTokenFetcher();

    this.initMyFieldServices();

  }

  // Fetch field service bindings on login success
  initMyFieldServices() {
    this.onLoginStatusChanged.subscribe(async loginResult => {
      if (loginResult) {
        this.fieldServiceAssignments = await this.getFieldServiceBindings(this.account.id, new Paginator<UserFieldServiceAssignment>({ ignorePagination: true }));
      }
    })
  }

  getCurrentUser() {
    return this.account;
  }

  goToLoginPage() {
    this.authProvider.goToLoginPage();
  }

  login(email: string, password: string): Promise<boolean> {
    return this.authProvider.login(email, password);
  }

  ssoLogin(accessToken: string, refreshToken: string): Promise<boolean> {
    return this.authProvider.ssoLogin(accessToken, refreshToken);
  }

  initSSOLoginFlow(): void {
    return this.authProvider.initSSOLoginFlow();
  }

  loginAnonymous(firstName?: string, lastName?: string): Promise<boolean> {
    return this.authProvider.loginAnonymous(firstName, lastName);
  }

  loginWithCustomToken(token: string) {
    return this.authProvider.loginWithCustomToken(token);
  }

  isLoggedIn(): Promise<boolean> | boolean {
    return this.authProvider.isLoggedIn();
  }

  logout() {
    this.authProvider.logout();
  }

  requestPIN(): Promise<string> {
    return this.authProvider.requestPIN();
  }

  downloadConfigurationQR() {
    // TODO migrate from open new tab with QR code in download QR code
    window.open(`${environment.restApi.infoEndpoint}/qrcode`, '_blank');
    /*this.restApiManager.getFile(
      `${environment.restApi.infoEndpoint}/qrcode`,
      `${this.account.company.name.toLowerCase().replace(' ', '-')}-configuration-QR.pdf`,
      this.account.authToken
    );*/
  }

  async getAllRoles(): Promise<Role[]> {
    const roles = await this.restApiManager.getAll<String>(
      null,
      `${environment.restApi.endpoint}/roles`,
      this.account.authToken,
    );
    return roles.map((role: string) => Role[role]);
  }

  async getRoles(userId: string): Promise<Role[]> {
    const roles = await this.restApiManager.getAll<String>(
      null,
      `${environment.restApi.endpoint}/users/${userId}/roles`,
      this.account.authToken,
    );
    return roles.map((role: string) => Role[role]);
  }

  public addRole(userId: string, role: Role) {
    return this.restApiManager.patch<void>(
      `${environment.restApi.endpoint}/users/${userId}/roles/${role}`,
      { },
      this.account.authToken
    );
  }

  public removeRole(userId: string, role: Role) {
    return this.restApiManager.delete(
      `${environment.restApi.endpoint}/users/${userId}/roles/${role}`,
      this.account.authToken
    );
  }

  getFieldServiceBindings(personnelId: string, paginator?: Paginator<UserFieldServiceAssignment>): Promise<UserFieldServiceAssignment[]> {
    return this.restApiManager.getAll<UserFieldServiceAssignment>(
      UserFieldServiceAssignment,
      `${environment.restApi.endpoint}/users/${personnelId}/field_services`,
      this.account.authToken,
      { paginator }
    );
  }

  public isAdmin(): boolean {
    return this.account.roles?.includes(Role.ROLE_ADMIN);
  }

  public isExpert(): boolean {
    return this.account.roles?.includes(Role.ROLE_EXPERT);
  }

  public isAdminOrExpert(): boolean {
    return true;
    return this.isAdmin() || this.isExpert();
  }

  public isFieldOperator(): boolean {
    return this.account.roles?.includes(Role.ROLE_FIELD);
  }

  // I am field manager if i am manager of ONE field service
  public isFieldManger(): boolean {
    return this.fieldServiceAssignments?.find(fieldService => fieldService.isManager) != null;
  }

  public isTeamLeaderOfFieldService(fieldServiceId: string): boolean {
    return this.fieldServiceAssignments?.find(fieldService => fieldService.isManager && fieldService.fieldService.id == fieldServiceId) != null;
  }

  // Get field service in which i am manager
  public getManagedFieldService(): FieldService {
    return this.fieldServiceAssignments?.find(fieldService => fieldService.isManager).fieldService;
  }

  public isTeamLeaderOrAdmin() {
    return this.isAdmin() || this.isFieldManger();
  }

}
